import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLanguageFlag } from '../utils/languageFlag';
import { motion } from 'framer-motion';
import {
  TrendingUp, Check, ArrowRight, Play,
  Package, ShoppingCart, Settings, Home, Inbox, BarChart2,
  RefreshCw, Search, Bell, ChevronRight, Star, Clock, Menu, X,
} from 'lucide-react';

const LAUNCH_END_DATE = new Date('2026-05-31T23:59:59');

function useLaunchDaysLeft() {
  const [daysLeft, setDaysLeft] = useState(() => {
    return Math.max(0, Math.ceil((LAUNCH_END_DATE.getTime() - Date.now()) / 86400000));
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setDaysLeft(Math.max(0, Math.ceil((LAUNCH_END_DATE.getTime() - Date.now()) / 86400000)));
    }, 60000);
    return () => clearInterval(timer);
  }, []);
  return daysLeft;
}

// ── Fade-up animation helper ──────────────────────────────────────────────────
const fadeUp = (delay = 0, duration = 0.6, y = 16) => ({
  initial: { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration, delay, ease: [0.21, 0.47, 0.32, 0.98] },
});

// ── Mini sparkline SVG (price trend) ─────────────────────────────────────────
const Sparkline: React.FC = () => (
  <svg viewBox="0 0 240 56" className="w-full h-14" preserveAspectRatio="none">
    <defs>
      <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M0 46 C28 42, 52 50, 80 36 C108 22, 132 44, 160 30 C188 16, 212 26, 240 12"
      fill="none" stroke="#14b8a6" strokeWidth="1.8" strokeLinecap="round"
    />
    <path
      d="M0 46 C28 42, 52 50, 80 36 C108 22, 132 44, 160 30 C188 16, 212 26, 240 12 L240 56 L0 56 Z"
      fill="url(#sg)"
    />
  </svg>
);

// ── Dashboard mockup ──────────────────────────────────────────────────────────
const DashboardMock: React.FC = () => {
  const sidebarItems = [
    { icon: <Home size={11} />, label: 'Dashboard', active: true },
    { icon: <Bell size={11} />, label: 'Alertes prix', badge: '5' },
    { icon: <BarChart2 size={11} />, label: 'Analyses' },
  ];
  const crmItems = [
    { icon: <ShoppingCart size={11} />, label: 'Ventes', badge: '3' },
    { icon: <Package size={11} />, label: 'Achats' },
    { icon: <Inbox size={11} />, label: 'Réception' },
    { icon: <TrendingUp size={11} />, label: 'Stock' },
  ];
  const bottomItems = [{ icon: <Settings size={11} />, label: 'Paramètres' }];

  const scans = [
    { name: 'Nike Air Max 90', results: '124 rés.', avg: '89 €', trend: '+5%', up: true },
    { name: 'PS5 Console',     results: '47 rés.',  avg: '380 €', trend: '-3%', up: false },
    { name: "Levi's 501",      results: '89 rés.',  avg: '32 €',  trend: '=',   up: null  },
    { name: 'MacBook Pro M1',  results: '12 rés.',  avg: '920 €', trend: '+2%', up: true },
  ];

  return (
    <div
      className="rounded-2xl overflow-hidden select-none pointer-events-none"
      style={{
        background: 'rgba(255,255,255,0.45)',
        border: '1px solid rgba(255,255,255,0.55)',
        boxShadow: 'var(--shadow-dashboard)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Top bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-white/70">
        <div className="flex items-center gap-1.5 w-28">
          <img src="/logo192.png" alt="SmartResell" className="w-5 h-5 rounded-md object-cover" />
          <span className="font-semibold text-gray-800" style={{ fontSize: 10 }}>SmartResell</span>
          <ChevronRight size={9} className="text-gray-400" />
        </div>
        <div className="flex-1 flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1">
          <Search size={9} className="text-gray-400" />
          <span className="text-gray-400" style={{ fontSize: 9 }}>Rechercher...</span>
          <span className="ml-auto text-gray-300 font-mono" style={{ fontSize: 8 }}>⌘K</span>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <RefreshCw size={10} className="text-gray-400" />
          <Bell size={10} className="text-gray-400" />
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
            <span className="text-white font-semibold" style={{ fontSize: 7 }}>G</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex" style={{ minHeight: 260 }}>
        {/* Sidebar */}
        <div className="w-28 border-r border-gray-100 bg-white/60 flex flex-col py-2 px-1.5 gap-0.5 flex-shrink-0">
          {sidebarItems.map((item) => (
            <div
              key={item.label}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${
                item.active ? 'bg-teal-50 text-teal-700' : 'text-gray-500'
              }`}
            >
              <span className={item.active ? 'text-teal-600' : 'text-gray-400'}>{item.icon}</span>
              <span style={{ fontSize: 9 }} className="flex-1 font-medium">{item.label}</span>
              {item.badge && (
                <span className="bg-teal-100 text-teal-700 rounded-full px-1" style={{ fontSize: 7 }}>{item.badge}</span>
              )}
            </div>
          ))}

          <div className="px-2 pt-2 pb-0.5">
            <span className="text-gray-400 font-medium uppercase tracking-wide" style={{ fontSize: 7 }}>CRM</span>
          </div>

          {crmItems.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-gray-500">
              <span className="text-gray-400">{item.icon}</span>
              <span style={{ fontSize: 9 }} className="flex-1 font-medium">{item.label}</span>
              {item.badge && (
                <span className="bg-amber-100 text-amber-700 rounded-full px-1" style={{ fontSize: 7 }}>{item.badge}</span>
              )}
            </div>
          ))}

          <div className="flex-1" />
          {bottomItems.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-gray-400">
              {item.icon}
              <span style={{ fontSize: 9 }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="flex-1 bg-gray-50/60 p-3 flex flex-col gap-2.5 min-w-0">
          {/* Greeting + actions */}
          <div>
            <p className="font-semibold text-gray-800 mb-1.5" style={{ fontSize: 11 }}>Bienvenue, Gabriel</p>
            <div className="flex flex-wrap gap-1">
              {[
                { label: '+ Nouvelle alerte', primary: true },
                { label: 'Scanner tout' },
                { label: 'Voir les ventes' },
                { label: 'Personnaliser', muted: true },
              ].map((b) => (
                <span
                  key={b.label}
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    b.primary
                      ? 'bg-teal-600 text-white'
                      : b.muted
                      ? 'text-gray-400'
                      : 'bg-white border border-gray-200 text-gray-600'
                  }`}
                  style={{ fontSize: 8 }}
                >
                  {b.label}
                </span>
              ))}
            </div>
          </div>

          {/* Cards row */}
          <div className="flex gap-2">
            {/* Price tracker card */}
            <div className="flex-1 bg-white rounded-xl border border-gray-100 p-2.5 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-500 font-medium" style={{ fontSize: 9 }}>Alertes actives</span>
                <span className="text-gray-400" style={{ fontSize: 8 }}>Ce mois</span>
              </div>
              <div className="flex items-baseline gap-1 mb-0.5">
                <span className="text-gray-800 font-bold" style={{ fontSize: 16 }}>5</span>
                <span className="text-gray-400" style={{ fontSize: 8 }}>alertes</span>
              </div>
              <div className="flex gap-2 mb-1">
                <span className="text-teal-600 font-medium" style={{ fontSize: 8 }}>↑ +1,247 résultats</span>
                <span className="text-red-500 font-medium" style={{ fontSize: 8 }}>Prix moyen 89 €</span>
              </div>
              <Sparkline />
            </div>

            {/* CRM card */}
            <div className="flex-1 bg-white rounded-xl border border-gray-100 p-2.5 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 font-medium" style={{ fontSize: 9 }}>CRM Ventes</span>
                <span className="text-gray-300" style={{ fontSize: 9 }}>⋯</span>
              </div>
              {[
                { label: 'À expédier',    value: '3',       cls: 'text-amber-600' },
                { label: 'En transit',    value: '2',       cls: 'text-sky-600'   },
                { label: 'Revenus mois',  value: '+245 €',  cls: 'text-teal-600'  },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-500" style={{ fontSize: 9 }}>{row.label}</span>
                  <span className={`font-semibold ${row.cls}`} style={{ fontSize: 9 }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent scans table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-2.5 py-1.5 border-b border-gray-50">
              <span className="text-gray-600 font-semibold" style={{ fontSize: 9 }}>Alertes récentes</span>
            </div>
            <table className="w-full" style={{ fontSize: 8 }}>
              <thead>
                <tr className="text-gray-400">
                  <th className="text-left px-2.5 py-1 font-medium">Alerte</th>
                  <th className="text-left px-2 py-1 font-medium">Résultats</th>
                  <th className="text-left px-2 py-1 font-medium">Prix moy.</th>
                  <th className="text-left px-2 py-1 font-medium">Tendance</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((row) => (
                  <tr key={row.name} className="border-t border-gray-50">
                    <td className="px-2.5 py-1.5 text-gray-700 font-medium">{row.name}</td>
                    <td className="px-2 py-1.5 text-gray-400">{row.results}</td>
                    <td className="px-2 py-1.5 text-gray-700 font-semibold">{row.avg}</td>
                    <td className="px-2 py-1.5">
                      <span className={`font-semibold ${
                        row.up === true ? 'text-teal-600' :
                        row.up === false ? 'text-red-500' : 'text-gray-400'
                      }`}>{row.trend}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};


// ── Main Landing Page ─────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goToApp = () => navigate('/app');
  const daysLeft = useLaunchDaysLeft();
  const featuresRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isFrench = typeof navigator !== 'undefined' && navigator.language.startsWith('fr');

  type PlanFeature = { label: string; strikethrough?: boolean; frOnly?: boolean };

  const plans: {
    id: string; name: string; monthlyPrice: number; strikeMonthlyPrice: number | null;
    highlight: boolean; popularBadge?: boolean;
    desc: string; cta: string;
    features: PlanFeature[];
    hasAnnual: boolean; annualPrice: number | null; annualTotal: number | null;
  }[] = [
    {
      id: 'free', name: 'Gratuit', monthlyPrice: 0, strikeMonthlyPrice: null,
      highlight: false,
      desc: 'Pour découvrir l\'application sans engagement.',
      cta: 'Commencer gratuitement',
      features: [
        { label: '10 ventes visibles' },
        { label: '10 achats visibles' },
        { label: '10 articles en stock traités' },
        { label: 'CRM illimité', strikethrough: true },
        { label: 'Scraping des prix', strikethrough: true },
        { label: 'Recherche de niches', strikethrough: true },
      ],
      hasAnnual: false, annualPrice: null, annualTotal: null,
    },
    {
      id: 'starter', name: 'Starter', monthlyPrice: 10, strikeMonthlyPrice: 18,
      highlight: false,
      desc: 'CRM Vinted complet pour gérer vos transactions.',
      cta: 'Choisir Starter',
      features: [
        { label: 'CRM complet illimité' },
        { label: 'Recadrage des bordereaux d\'expédition' },
        { label: 'Traitement QR code & code de retrait colis', frOnly: true },
        { label: 'Comptabilité intégrée' },
        { label: 'Scraping des prix des articles', strikethrough: true },
        { label: 'Recherche de niches', strikethrough: true },
      ],
      hasAnnual: true, annualPrice: 7.5, annualTotal: 90,
    },
    {
      id: 'hunter', name: 'Hunter', monthlyPrice: 18, strikeMonthlyPrice: 25,
      highlight: true, popularBadge: true,
      desc: 'CRM + Scraping des prix pour maximiser vos reventes.',
      cta: 'Choisir Hunter',
      features: [
        { label: 'Tout du plan Starter' },
        { label: '25 scrapings Vinted inclus (niches & prix)' },
        { label: 'Historique & analyse avancée par alerte' },
      ],
      hasAnnual: true, annualPrice: 13.5, annualTotal: 162,
    },
    {
      id: 'business', name: 'Business', monthlyPrice: 35, strikeMonthlyPrice: null,
      highlight: false,
      desc: 'Pour les revendeurs professionnels à haut volume.',
      cta: 'Choisir Business',
      features: [
        { label: 'Tout du plan Hunter' },
        { label: '100 recherches de prix incluses' },
      ],
      hasAnnual: false, annualPrice: null, annualTotal: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden font-body">

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <span className="text-xl leading-none">{getLanguageFlag()}</span>
            <img src="/logo192.png" alt="SmartResell" className="w-9 h-9 rounded-xl object-cover" />
            <span className="text-lg font-semibold tracking-tight bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent font-body">
              SmartResell
            </span>
          </div>

          {/* Nav links (desktop) */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo(featuresRef)} className="text-sm text-gray-500 hover:text-gray-800 transition-colors font-body bg-transparent border-0 cursor-pointer">Fonctionnalités</button>
            <button onClick={() => scrollTo(pricingRef)} className="text-sm text-gray-500 hover:text-gray-800 transition-colors font-body bg-transparent border-0 cursor-pointer">Tarifs</button>
            <a href="/blog" className="text-sm text-gray-500 hover:text-gray-800 transition-colors font-body">Blog</a>
            <a href="/contact" className="text-sm text-gray-500 hover:text-gray-800 transition-colors font-body">Contact</a>
          </div>

          {/* CTA + hamburger */}
          <div className="flex items-center gap-2">
            <button onClick={goToApp} className="hidden sm:block text-sm text-gray-600 hover:text-teal-700 font-medium font-body transition-colors">
              Se connecter
            </button>
            <button
              onClick={goToApp}
              className="hidden sm:block rounded-full px-5 py-2 text-sm font-medium font-body text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-sm transition-all"
            >
              Essayer gratuitement
            </button>
            {/* Bouton CTA court sur mobile */}
            <button
              onClick={goToApp}
              className="sm:hidden rounded-full px-4 py-2 text-sm font-medium font-body text-white bg-gradient-to-r from-teal-500 to-cyan-500 shadow-sm"
            >
              Essayer
            </button>
            {/* Hamburger mobile */}
            <button
              className="md:hidden p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(v => !v)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Menu mobile déroulant */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 px-5 py-4 flex flex-col gap-1">
            <button onClick={() => scrollTo(featuresRef)} className="text-sm text-gray-700 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors font-body text-left bg-transparent border-0 cursor-pointer">Fonctionnalités</button>
            <button onClick={() => scrollTo(pricingRef)} className="text-sm text-gray-700 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors font-body text-left bg-transparent border-0 cursor-pointer">Tarifs</button>
            <a href="/blog" className="text-sm text-gray-700 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors font-body">Blog</a>
            <a href="/contact" className="text-sm text-gray-700 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors font-body">Contact</a>
            <div className="h-px bg-gray-100 my-2" />
            <button onClick={goToApp} className="text-sm text-gray-700 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors text-left font-body">
              Se connecter
            </button>
            <button
              onClick={goToApp}
              className="mt-1 w-full py-3 rounded-xl text-sm font-semibold font-body text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 transition-all shadow-sm"
            >
              Commencer gratuitement →
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO (fullscreen) ───────────────────────────────────────────────── */}
      <section className="h-screen flex flex-col overflow-hidden hero-mesh relative">
        {/* Decorative blobs */}
        <div className="absolute top-1/4 left-1/3 w-80 h-80 bg-teal-200 rounded-full blur-3xl opacity-20 pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-200 rounded-full blur-3xl opacity-15 pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-start pt-24 px-5 sm:px-6 w-full overflow-hidden">

          {/* Badge */}
          <motion.div {...fadeUp(0, 0.5, 10)}>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-white/80 px-4 py-1.5 text-sm text-teal-700 font-body mb-6 shadow-sm">
              <Star className="w-3.5 h-3.5 fill-teal-400 text-teal-400" />
              CRM et scraping Vinted ✨
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            {...fadeUp(0.1, 0.6, 16)}
            className="text-center font-display text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] leading-[1.05] md:leading-[0.95] tracking-tight text-gray-900 max-w-2xl"
          >
            Vendez sur Vinted,{' '}
            <em className="font-display italic not-italic" style={{ fontStyle: 'italic' }}>
              plus&nbsp;intelligemment
            </em>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            {...fadeUp(0.2, 0.6, 16)}
            className="mt-4 sm:mt-5 text-center text-sm sm:text-base md:text-lg text-gray-500 max-w-xl leading-relaxed font-body px-2"
          >
            Gérez vos ventes, vos achats, votre stock et votre réception — avec le scraping Vinted intégré pour maximiser chaque revente.
          </motion.p>

          {/* CTA buttons */}
          <motion.div {...fadeUp(0.3, 0.6, 16)} className="mt-6 flex items-center gap-3">
            <button
              onClick={goToApp}
              className="rounded-full px-6 sm:px-7 py-3 text-sm font-semibold font-body text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-lg hover:shadow-teal-200 transition-all"
            >
              Commencer gratuitement
            </button>
            <button
              onClick={goToApp}
              className="h-11 w-11 rounded-full bg-white shadow-md hover:bg-white/80 flex items-center justify-center border-0 transition-all"
              title="Voir une démo"
            >
              <Play className="h-4 w-4 fill-gray-800 text-gray-800 ml-0.5" />
            </button>
          </motion.div>

          {/* Dashboard mockup — caché sur très petit écran */}
          <motion.div
            {...fadeUp(0.5, 0.8, 30)}
            className="mt-8 w-full max-w-5xl px-2 sm:px-4"
          >
            <DashboardMock />
          </motion.div>

        </div>
      </section>

      {/* ── FEATURES ───────────────────────────────────────────────────────── */}
      <section ref={featuresRef} className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">

          {/* Titre centré */}
          <h2 className="font-display text-4xl md:text-5xl text-gray-900 text-center mb-12">
            Tout ce qu'il vous <em style={{ fontStyle: 'italic' }}>faut</em>
          </h2>

          {/* 4 blocs en grille 2×2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                icon: <TrendingUp className="w-5 h-5 text-teal-600" />,
                title: 'Scraping des prix en temps réel',
                desc: "Suivez l'évolution des prix au fil du temps grâce à un historique détaillé, analysez les tendances, identifiez les niches rentables et ne ratez plus aucune opportunité",
              },
              {
                icon: <ShoppingCart className="w-5 h-5 text-teal-600" />,
                title: 'Gestion des ventes',
                desc: "Imprimez vos bordereaux recadrés automatiquement, calculez vos marges depuis votre stock et générez vos factures de vente sans effort",
              },
              {
                icon: <Package className="w-5 h-5 text-teal-600" />,
                title: 'Achats & Stock',
                desc: "Centralisez vos achats, gérez votre stock en temps réel, accédez aux QR codes de récupération de vos colis en un clic et automatisez la génération de vos factures d'achats",
              },
              {
                icon: <BarChart2 className="w-5 h-5 text-teal-600" />,
                title: 'Comptabilité',
                desc: "Suivez votre chiffre d'affaires Vinted, calculez vos marges en temps réel et pilotez votre performance financière de façon entièrement automatisée.",
              },
            ].map((f, i) => (
              <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                    {f.icon}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 font-body">{f.title}</h3>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed font-body">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Séparateur + titre vidéo */}
          <div className="mt-20 text-center">
            <h2 className="font-display text-3xl md:text-4xl text-gray-900 mb-2">
              Une démo vaut <em style={{ fontStyle: 'italic' }}>mille mots</em>
            </h2>
            <p className="text-gray-400 text-sm font-body mb-8">Découvrez SmartResell en action</p>
          </div>

          {/* Vidéo YouTube */}
          <div className="rounded-2xl overflow-hidden shadow-2xl shadow-teal-100/50 aspect-video">
            <iframe
              src="https://www.youtube.com/embed/YjnRq7zkZmc?rel=0&modestbranding=1"
              title="SmartResell — Démo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>

        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────────────────────── */}
      <section ref={pricingRef} className="py-24 px-6 bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-4xl md:text-5xl text-gray-900 mb-4">
              Des tarifs <em style={{ fontStyle: 'italic' }}>transparents</em>
            </h2>
            <p className="text-lg text-gray-500 font-body mb-8">
              Commencez gratuitement. Upgrader quand vous le souhaitez.
            </p>
            {/* Toggle mensuel / annuel */}
            <div className="inline-flex items-center gap-3 bg-white border border-gray-200 rounded-full px-2 py-1.5 shadow-sm">
              <button onClick={() => setBillingAnnual(false)} className={`px-4 py-1.5 rounded-full text-sm font-medium font-body transition-all ${!billingAnnual ? 'bg-teal-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Mensuel</button>
              <button onClick={() => setBillingAnnual(true)} className={`px-4 py-1.5 rounded-full text-sm font-medium font-body transition-all flex items-center gap-1.5 ${billingAnnual ? 'bg-teal-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                Annuel
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${billingAnnual ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-700'}`}>-25%</span>
              </button>
            </div>
          </div>

          {(() => {
            const renderCard = (plan: typeof plans[0], showAnnual: boolean) => {
              const displayPrice = showAnnual && plan.annualPrice != null ? plan.annualPrice : plan.monthlyPrice;
              const strikePrice = showAnnual ? null : plan.strikeMonthlyPrice;
              const visibleFeatures = plan.features.filter(f => !f.frOnly || isFrench);
              return (
                <div className={`rounded-2xl p-6 flex flex-col h-full transition-all duration-300 ${
                  plan.highlight
                    ? 'bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-xl shadow-teal-200'
                    : 'bg-white border border-gray-100 shadow-md'
                }`}>
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className={`text-lg font-bold font-body ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                      {plan.popularBadge && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-yellow-400 text-yellow-900">⭐ Populaire</span>
                      )}
                    </div>
                    <p className={`text-xs mb-3 font-body leading-relaxed ${plan.highlight ? 'text-teal-100' : 'text-gray-500'}`}>{plan.desc}</p>
                    {strikePrice != null && (
                      <div className="text-sm font-body mb-0.5">
                        <span className={`line-through ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>{strikePrice % 1 === 0 ? strikePrice : strikePrice.toFixed(2).replace('.', ',')}€</span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className={`text-3xl font-extrabold font-display ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                        {displayPrice === 0 ? '0€' : `${displayPrice % 1 === 0 ? displayPrice : displayPrice.toFixed(2).replace('.', ',')}€`}
                      </span>
                      {displayPrice > 0 && <span className={`text-xs font-body ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>/mois</span>}
                    </div>
                    {showAnnual && plan.annualTotal != null && (
                      <p className={`text-xs mt-1 font-body ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>
                        soit {plan.annualTotal % 1 === 0 ? plan.annualTotal : plan.annualTotal.toFixed(2).replace('.', ',')}€/an
                      </p>
                    )}
                    {!plan.hasAnnual && plan.id !== 'free' && billingAnnual && (
                      <p className={`text-xs mt-1 font-body ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>Mensuel uniquement</p>
                    )}
                  </div>
                  <ul className="space-y-2 mb-5 flex-1">
                    {visibleFeatures.map((feat) => (
                      <li key={feat.label} className="flex items-start gap-2">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${feat.strikethrough ? plan.highlight ? 'bg-white/10' : 'bg-gray-100' : plan.highlight ? 'bg-white/20' : 'bg-teal-50'}`}>
                          <Check className={`w-2.5 h-2.5 ${feat.strikethrough ? plan.highlight ? 'text-white/30' : 'text-gray-300' : plan.highlight ? 'text-white' : 'text-teal-600'}`} />
                        </div>
                        <span className={`text-xs font-body ${feat.strikethrough ? plan.highlight ? 'line-through text-white/40' : 'line-through text-gray-400' : plan.highlight ? 'text-teal-50' : 'text-gray-600'}`}>{feat.label}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={goToApp} className={`w-full py-2.5 rounded-xl text-sm font-semibold font-body transition-all ${plan.highlight ? 'bg-white text-teal-700 hover:bg-teal-50 shadow-lg' : plan.id === 'free' ? 'border-2 border-gray-200 text-gray-700 hover:border-teal-300 hover:text-teal-700' : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 shadow-md'}`}>
                    {plan.cta}
                  </button>
                </div>
              );
            };

            const showAnnual = billingAnnual;
            return (
              <>
                {/* Mobile : empilement avec encadrement orange autour Starter+Hunter */}
                <div className="lg:hidden flex flex-col gap-4">
                  {renderCard(plans[0], showAnnual)}
                  <div className="border-2 border-orange-400 rounded-2xl p-3 flex flex-col gap-3 bg-orange-50/20">
                    <div className="text-center">
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full px-4 py-1.5 text-xs font-semibold font-body shadow-md">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                        {daysLeft > 0 ? `Offre lancement — encore ${daysLeft} jour${daysLeft > 1 ? 's' : ''}` : 'Offre terminée'}
                      </div>
                    </div>
                    {renderCard(plans[1], showAnnual)}
                    {renderCard(plans[2], showAnnual)}
                  </div>
                  {renderCard(plans[3], showAnnual)}
                </div>

                {/* Desktop : 4 colonnes égales + encadrement orange décoratif absolu */}
                <div className="hidden lg:block relative" style={{ paddingTop: '4.5rem', paddingBottom: '1.5rem' }}>
                  {/* Encadrement orange élargi au-dessus des cartes (z-20 > z-10) */}
                  <div
                    className="absolute border-2 border-orange-400 rounded-3xl pointer-events-none"
                    style={{ left: 'calc(25% - 6px)', right: 'calc(25% - 6px)', top: '0', bottom: '0', zIndex: 20, background: 'rgba(255, 237, 213, 0.12)' }}
                  />
                  {/* Compteur centré en haut de l'encadrement */}
                  <div className="absolute left-1/2 -translate-x-1/2 z-10 whitespace-nowrap" style={{ top: '1rem' }}>
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full px-5 py-2 text-sm font-semibold font-body shadow-md">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      {daysLeft > 0
                        ? `Offre de lancement — encore ${daysLeft} jour${daysLeft > 1 ? 's' : ''} (31 mai 2026)`
                        : 'Offre de lancement terminée'}
                    </div>
                  </div>
                  {/* 4 cartes égales */}
                  <div className="grid grid-cols-4 gap-6 items-stretch relative z-10">
                    {plans.map(plan => (
                      <div key={plan.id} className="flex flex-col">
                        {renderCard(plan, showAnnual)}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* ── BLOG ───────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="inline-block text-xs font-bold text-teal-600 bg-teal-50 px-3 py-1 rounded-full mb-3 uppercase tracking-widest">Blog</span>
              <h2 className="font-display text-3xl md:text-4xl text-gray-900 leading-tight">
                Conseils pour <em style={{ fontStyle: 'italic' }}>bien revendre</em>
              </h2>
            </div>
            <a
              href="/blog"
              className="text-sm font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1.5 transition-colors font-body"
            >
              Voir tous les articles
              <ChevronRight size={15} />
            </a>
          </div>

          {/* Article card */}
          <a
            href="/blog/grossistes-fournisseurs-vinted"
            className="group block bg-gray-50 border border-gray-100 rounded-2xl p-7 hover:border-teal-200 hover:shadow-lg transition-all duration-200 no-underline"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="text-xs font-semibold text-teal-600 bg-teal-100 px-2.5 py-1 rounded-full font-body">
                    Stratégie Vinted
                  </span>
                  <span className="text-xs text-gray-400 font-body">6 min de lecture</span>
                  <span className="text-xs text-gray-400 font-body">20 avril 2026</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors leading-snug font-body">
                  📦 Grossistes & fournisseurs Vinted : ce que personne ne te dit vraiment
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed font-body">
                  Avant de commander pour 5 000 € de stock, lis ça. On te dit tout sur les vrais fournisseurs privés, les arnaques à fuir et pourquoi 90 % des "grossistes" vantés sur les réseaux sont des pièges.
                </p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border border-gray-100 group-hover:bg-teal-50 group-hover:border-teal-200 flex items-center justify-center transition-colors">
                <ChevronRight size={18} className="text-gray-400 group-hover:text-teal-600 transition-colors" />
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <footer className="py-12 px-6 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo192.png" alt="SmartResell" className="w-7 h-7 rounded-lg object-cover" />
            <span className="font-semibold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent font-body">SmartResell</span>
          </div>
          <p className="text-sm text-gray-400 font-body">© 2026 SmartResell — Scraping des prix & CRM Vinted</p>
          <div className="flex items-center gap-6">
            <a href="/blog" className="text-sm text-gray-400 hover:text-teal-600 transition-colors font-body">Blog</a>
            <a href="/cgu" className="text-sm text-gray-400 hover:text-teal-600 transition-colors font-body">Mentions légales</a>
            <a href="/confidentialite" className="text-sm text-gray-400 hover:text-teal-600 transition-colors font-body">Confidentialité</a>
            <a href="/contact" className="text-sm text-gray-400 hover:text-teal-600 transition-colors font-body">Contact</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
